import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
const PrivacyPolicy = () => {
    const navigator = useNavigate();
    return (
        <>
            <Helmet>
                <title>Privacy Policy | TLK</title>
            </Helmet>
            {/* start: privacy page */}
            <div className="min-h-screen gradient-bg-1">
                <div className="fixed w-full top-0 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3 z-10">
                    <button className="text-foreground"
                        onClick={() => navigator(-1)}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-arrow-left w-6 h-6"
                        >
                            <path d="m12 19-7-7 7-7" />
                            <path d="M19 12H5" />
                        </svg>
                    </button>
                    <h1 className="text-lg font-semibold text-foreground">Privacy Policy</h1>
                </div>
                <div
                    className="p-6 pt-[100px] max-w-2xl mx-auto"
                    style={{ opacity: 1, transform: "none" }}
                >
                    <p className="text-sm text-muted-foreground mb-6">
                        Last updated: December 2025
                    </p>
                    <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 mb-6">
                        <div className="flex items-center gap-3 mb-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-shield w-6 h-6 text-primary"
                            >
                                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                            </svg>
                            <h3 className="font-semibold text-foreground">Our Privacy Promise</h3>
                        </div>
                        <p className="text-sm text-foreground/90">
                            <strong>Your information is private—always and forever.</strong> Your
                            conversation history belongs to you and is deletable anytime. We will
                            never sell, share, or expose your personal data.
                        </p>
                    </div>
                    <div className="space-y-6 text-foreground/90 text-sm leading-relaxed">
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                1. Information We Collect
                            </h2>
                            <p className="mb-2">We collect the following types of information:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                    <strong>Account Information:</strong> Email address, date of birth
                                    (for age verification)
                                </li>
                                <li>
                                    <strong>Payment Information:</strong> Processed securely through
                                    third-party providers (Google Pay, PayPal, Cash App, Venmo). We do
                                    not store payment card details.
                                </li>
                                <li>
                                    <strong>Usage Data:</strong> Interaction patterns, feature usage,
                                    session duration
                                </li>
                                <li>
                                    <strong>Conversation History:</strong> Messages, voice calls, and
                                    media exchanged within the Service
                                </li>
                                <li>
                                    <strong>Device Information:</strong> Browser type, operating system,
                                    device identifiers
                                </li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                2. How We Use Your Information
                            </h2>
                            <p className="mb-2">We use your information to:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>Provide and improve the Service</li>
                                <li>Process payments and manage subscriptions</li>
                                <li>Verify your age and identity</li>
                                <li>Communicate with you about your account</li>
                                <li>Ensure security and prevent fraud</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                3. Data Ownership &amp; Your Rights
                            </h2>
                            <div className="bg-card/50 rounded-xl p-4 border border-border">
                                <p className="font-medium text-foreground mb-2">
                                    Your history is yours.
                                </p>
                                <p className="mb-3">
                                    All conversation data—messages, voice recordings, images—belongs to
                                    you. You have complete control over your data with the following
                                    rights:
                                </p>
                                <ul className="list-disc list-inside space-y-1 ml-2">
                                    <li>
                                        <strong>Access:</strong> View all data we hold about you
                                    </li>
                                    <li>
                                        <strong>Delete:</strong> Permanently erase your conversation
                                        history anytime
                                    </li>
                                    <li>
                                        <strong>Export:</strong> Request a copy of your data
                                    </li>
                                    <li>
                                        <strong>Account Deletion:</strong> Completely remove your account
                                        and all associated data
                                    </li>
                                </ul>
                            </div>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                4. Data Storage &amp; Security
                            </h2>
                            <p>
                                We implement industry-standard security measures including encryption
                                in transit (TLS) and at rest to protect your information. Your data is
                                stored on secure servers with restricted access.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                5. What We Never Do
                            </h2>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                    We <strong>never</strong> sell your personal data to third parties
                                </li>
                                <li>
                                    We <strong>never</strong> share your conversation content with
                                    advertisers
                                </li>
                                <li>
                                    We <strong>never</strong> use your private messages for training AI
                                    models
                                </li>
                                <li>
                                    We <strong>never</strong> expose your identity or usage to other
                                    users
                                </li>
                            </ul>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                6. Third-Party Services
                            </h2>
                            <p className="mb-2">We use trusted third-party services for:</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                                <li>
                                    <strong>Payment Processing:</strong> Google Pay, PayPal, Cash App,
                                    Venmo
                                </li>
                                <li>
                                    <strong>Analytics:</strong> Anonymous usage statistics to improve
                                    the Service
                                </li>
                                <li>
                                    <strong>Infrastructure:</strong> Secure cloud hosting providers
                                </li>
                            </ul>
                            <p className="mt-2">
                                These providers have their own privacy policies and are contractually
                                bound to protect your data.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                7. Data Retention
                            </h2>
                            <p>
                                We retain your data only as long as your account is active or as
                                needed to provide the Service. When you delete your data or account,
                                it is permanently removed from our systems within 30 days, except
                                where required by law.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                8. Age Verification
                            </h2>
                            <p>
                                We collect date of birth solely to verify you are 18 or older. This
                                information is used only for age verification and is protected with
                                the same security as all other personal data.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                9. Cookies &amp; Tracking
                            </h2>
                            <p>
                                We use essential cookies to maintain your session and preferences. We
                                do not use third-party advertising cookies or cross-site tracking.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                10. Changes to This Policy
                            </h2>
                            <p>
                                We may update this Privacy Policy from time to time. We will notify
                                you of significant changes via email or in-app notification.
                            </p>
                        </section>
                        <section>
                            <h2 className="text-lg font-semibold text-foreground mb-3">
                                11. Contact Us
                            </h2>
                            <p>
                                For privacy-related questions or to exercise your data rights, contact
                                us at support@syntimacy.co
                            </p>
                        </section>
                    </div>
                    <div className="mt-8 pt-6 border-t border-border">
                        <p className="text-xs text-muted-foreground text-center">
                            Copyright 2026 Syntimacy LLC. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>

            {/* end: privacy page */}
        </>
    );
}

export default PrivacyPolicy;
